import React, { useMemo } from 'react';
import {
  INSIGHT_HEADLINE,
  INSIGHT_NARRATIVE,
  INSIGHT_META,
} from './landing-signal-flow-data';

interface LandingInsightNarrativeProps {
  /** 0..1 — progress within the Insight stage (stageFrac from ScrollyHero). */
  stageFrac: number;
  /** Render a smaller, denser variant — used on mobile so the tile fits
   *  above the StagePips bar without bleeding. */
  compact?: boolean;
}

/**
 * AI-insight block that reveals in three phases as the user scrolls through
 * the Insight stage. Phase windows are packed into the wrapper's hold band
 * (stageFrac 0.10..0.90) so the meta line reaches full opacity with time
 * to spare before the wrapper's exit fade begins:
 *   A: headline:  stageFrac in [0.10, 0.28] (word-by-word fade + translateY)
 *   B: narrative: stageFrac in [0.28, 0.55] (full-paragraph fade-in)
 *   C: meta:      stageFrac in [0.55, 0.78] (mono footer fade-in)
 *
 * Below 0.10 the whole block is hidden. From 0.78 to 0.90 the full tile
 * holds fully visible — that's the "dwell" beat at the end of the step
 * before the wrapper exits.
 */
export const LandingInsightNarrative: React.FC<LandingInsightNarrativeProps> = ({ stageFrac, compact = false }) => {
  const headlineWords = useMemo(() => INSIGHT_HEADLINE.split(' '), []);

  if (stageFrac < 0.10) {
    return null;
  }

  const narrativeT = progressBetween(stageFrac, 0.28, 0.55);
  const metaT      = progressBetween(stageFrac, 0.55, 0.78);

  return (
    <div
      style={{
        background: 'rgba(10,10,10,0.72)',
        border: '1px solid rgba(255,255,255,0.08)',
        backdropFilter: 'blur(8px)',
        padding: compact ? '12px 14px' : '18px 22px',
        maxWidth: 560,
        fontFamily: "'Inter', system-ui, sans-serif",
      }}
    >
      <p
        style={{
          fontSize: compact ? 13 : 14,
          lineHeight: 1.45,
          fontWeight: 600,
          color: '#fff',
          marginBottom: compact ? 6 : 10,
        }}
      >
        {headlineWords.map((word, i) => {
          // Keep every word's reveal inside the documented [0.10, 0.28] window:
          // stagger starts across (0.18 - WORD_REVEAL) so the last word ends at 0.28.
          const WORD_REVEAL = 0.03;
          const staggerSpan = 0.18 - WORD_REVEAL;
          const lastIdx = Math.max(headlineWords.length - 1, 1);
          const wordStart = 0.10 + (i / lastIdx) * staggerSpan;
          const wordEnd = wordStart + WORD_REVEAL;
          const t = progressBetween(stageFrac, wordStart, wordEnd);
          return (
            <span
              key={i}
              style={{
                display: 'inline-block',
                opacity: t,
                transform: `translateY(${(1 - t) * 8}px)`,
                marginRight: 4,
                transition: 'opacity .15s ease, transform .15s ease',
              }}
            >
              {word}
            </span>
          );
        })}
      </p>

      <p
        style={{
          fontSize: compact ? 12 : 13,
          lineHeight: 1.5,
          color: '#8a8a8a',
          marginBottom: compact ? 8 : 14,
          opacity: narrativeT,
          transform: `translateY(${(1 - narrativeT) * 12}px)`,
          transition: 'opacity .25s ease, transform .25s ease',
        }}
      >
        {INSIGHT_NARRATIVE}
      </p>

      <div
        style={{
          fontFamily: "'JetBrains Mono', ui-monospace, monospace",
          fontSize: compact ? 9 : 10,
          color: '#505050',
          letterSpacing: '0.02em',
          opacity: metaT,
          transition: 'opacity .25s ease',
        }}
      >
        {INSIGHT_META}
      </div>
    </div>
  );
};

/** Linear progress from `start` to `end`. Clamped 0..1. */
function progressBetween(t: number, start: number, end: number): number {
  if (t <= start) return 0;
  if (t >= end) return 1;
  return (t - start) / (end - start);
}
